import React, { useEffect, useState } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import {
    reduxForm,
    Field,
    FieldArray,
    getFormValues
} from 'redux-form';

import ContentCard from '../common/template/contentCard';
import ContentCardBody from '../common/template/contentCardBody';
import ContentCardFooter from '../common/template/contentCardFooter';
import Row from '../common/layout/row';
import Grid from '../common/layout/grid';
import Button from '../common/button/button';
import Form from '../common/form/form';
import LabelAndInput from '../common/form/labelAndInput';
import LabelAndInputDate from '../common/form/labelAndInputDate';
import LabelAndInputNumber from '../common/form/labelAndInputNumber';
import LabelAndTextarea from '../common/form/labelAndTextarea';
import LabelAndCheckbox from '../common/form/labelAndCheckbox';
import Select from '../common/form/select';
import ClienteForm from '../cliente/clienteForm';
import FormatUtils from '../common/formatUtils/FormatUtils';

import {
    setModoTela,
    initForm,
    getOpcoes,
    getAreasCliente,
    getHistorico,
    alterarStatus
} from './coletaActions';

import {
    setModoTela as setModoTelaCliente,
    initForm as initFormCliente,
    salvar as salvarCliente
} from '../cliente/clienteActions';

import {
    getLista as getListaCidade
} from '../cidade/cidadeActions';

const normalizarNumero = valor => {
    const texto = (valor || 0).toString();

    return Number(
        texto.includes(',')
            ? texto
                .replace(/\./g, '')
                .replace(',', '.')
            : texto
    ) || 0;
};

const formatarMoeda = valor =>
    `R$ ${FormatUtils.formatarValorTela(valor || 0, 2)}`;

const calcularValorTotal = area =>
    Math.round(
        normalizarNumero(area.area_gps) *
        normalizarNumero(area.preco_grid) *
        100
    ) / 100;

const calcularTamanhoGrid = area => {
    const areaGps = normalizarNumero(area.area_gps);
    const amostras = normalizarNumero(area.amostras);

    return amostras > 0
        ? Math.round((areaGps / amostras) * 10000) / 10000
        : 0;
};

const calcularValorFinal = area =>
    Math.round(
        (
            calcularValorTotal(area) -
            normalizarNumero(area.valor_desconto)
        ) *
        100
    ) / 100;

const STATUS = {
    AGUARDANDO_APROVACAO: 'AGUARDANDO_APROVACAO',
    REJEITADO: 'REJEITADO',
    AGUARDANDO_COLETA: 'APROVADO_AGUARDANDO_COLETA',
    COLETADO: 'COLETADO',
    LABORATORIO: 'ENVIADO_LABORATORIO',
    FINALIZADA: 'FINALIZADA'
};

const STATUS_CONFIG = {
    [STATUS.AGUARDANDO_APROVACAO]: {
        label: 'Aguardando aprovação',
        cor: '#92400e',
        fundo: '#fef3c7'
    },
    [STATUS.REJEITADO]: {
        label: 'Rejeitada',
        cor: '#991b1b',
        fundo: '#fee2e2'
    },
    [STATUS.AGUARDANDO_COLETA]: {
        label: 'Aprovada — aguardando coleta',
        cor: '#075985',
        fundo: '#e0f2fe'
    },
    [STATUS.COLETADO]: {
        label: 'Coletada',
        cor: '#065f46',
        fundo: '#d1fae5'
    },
    [STATUS.LABORATORIO]: {
        label: 'Enviada para o laboratório',
        cor: '#5b21b6',
        fundo: '#ede9fe'
    },
    [STATUS.FINALIZADA]: {
        label: 'Finalizada',
        cor: '#14532d',
        fundo: '#dcfce7'
    }
};

const PARCELAS = [1, 2, 3, 4, 5, 6].map(numero => ({
    id: numero,
    valor: numero === 1 ? '1 vez' : `${numero} vezes`
}));

const ETAPAS_FLUXO = [
    {
        status: STATUS.AGUARDANDO_APROVACAO,
        label: 'Aprovação',
        icon: 'fas fa-clipboard-check'
    },
    {
        status: STATUS.AGUARDANDO_COLETA,
        label: 'Coleta',
        icon: 'fas fa-map-marked-alt'
    },
    {
        status: STATUS.COLETADO,
        label: 'Coletada',
        icon: 'fas fa-check-circle'
    },
    {
        status: STATUS.LABORATORIO,
        label: 'Laboratório',
        icon: 'fas fa-flask'
    },
    {
        status: STATUS.FINALIZADA,
        label: 'Finalizada',
        icon: 'fas fa-flag-checkered'
    }
];

const FluxoColeta = ({ status }) => {
    const indiceAtual = ETAPAS_FLUXO.findIndex(
        etapa => etapa.status === status
    );
    const rejeitada = status === STATUS.REJEITADO;

    return (
        <div
            style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 8,
                marginBottom: 18
            }}
        >
            {ETAPAS_FLUXO.map((etapa, index) => {
                const concluida =
                    !rejeitada && index < indiceAtual;
                const atual =
                    !rejeitada && index === indiceAtual;
                const cor = concluida
                    ? '#166534'
                    : atual
                        ? '#075bb5'
                        : '#64748b';
                const fundo = concluida
                    ? '#dcfce7'
                    : atual
                        ? '#eaf4ff'
                        : '#f1f5f9';

                return (
                    <div
                        key={etapa.status}
                        style={{
                            flex: '1 1 145px',
                            minWidth: 135,
                            padding: '11px 12px',
                            borderRadius: 10,
                            border: `1px solid ${cor}33`,
                            background: fundo,
                            color: cor,
                            fontWeight: atual || concluida
                                ? 700
                                : 500
                        }}
                    >
                        <i
                            className={
                                concluida
                                    ? 'fas fa-check'
                                    : etapa.icon
                            }
                            style={{ marginRight: 7 }}
                        />

                        {etapa.label}
                    </div>
                );
            })}
        </div>
    );
};

const renderAreas = ({
    fields,
    readOnly,
    areasCliente,
    consultores,
    idUsuario,
    status,
    excluir,
    usuarioResponsavel,
    registrarExclusaoArea,
    change
}) => {
    const mostrarLaboratorio = [
        STATUS.LABORATORIO,
        STATUS.FINALIZADA
    ].includes(status);

    const laboratorioReadOnly =
        excluir ||
        status !== STATUS.LABORATORIO
            ? 'readOnly'
            : '';

    return (
    <div style={{ width: '100%' }}>
        <div
            style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 10,
                marginBottom: 12
            }}
        >
            <div>
                <div
                    style={{
                        fontSize: 18,
                        fontWeight: 700,
                        color: '#062042'
                    }}
                >
                    <i
                        className='fas fa-map-marked-alt'
                        style={{ marginRight: 8 }}
                    />
                    Áreas da coleta
                </div>

                <small className='text-muted'>
                    Informe os dados técnicos e comerciais de cada área.
                </small>
            </div>

            {!readOnly &&
                <button
                    type='button'
                    className='btn btn-success'
                    disabled={!areasCliente.length}
                    onClick={() => fields.push({
                        id_consultor: idUsuario,
                        id_cliente_area: null,
                        area_gps: '',
                        amostras: '',
                        tamanho_grid: 0,
                        profundidade: '',
                        zona_manejo: false,
                        preco_grid: '',
                        valor_total: 0,
                        valor_desconto: '0,00',
                        valor_final: 0,
                        observacao: ''
                    })}
                    style={{
                        minHeight: 40,
                        borderRadius: 9,
                        fontWeight: 600
                    }}
                >
                    <i
                        className='fas fa-plus'
                        style={{ marginRight: 7 }}
                    />
                    Adicionar área
                </button>}
        </div>

        {!fields.length &&
            <div
                style={{
                    padding: 24,
                    borderRadius: 10,
                    border: '1px dashed #b9c4d0',
                    background: '#f8fafc',
                    color: '#6b7280',
                    textAlign: 'center'
                }}
            >
                <i
                    className='fas fa-seedling'
                    style={{
                        display: 'block',
                        marginBottom: 8,
                        fontSize: 24
                    }}
                />
                Selecione o cliente e adicione uma área para coleta.
            </div>}

        {fields.map((area, index) => (
            <div
                key={area}
                style={{
                    position: 'relative',
                    marginBottom: 16,
                    padding: '16px 14px 4px',
                    borderRadius: 12,
                    border: '1px solid #dce3eb',
                    background: '#fff',
                    boxShadow: '0 3px 10px rgba(15, 35, 60, .06)'
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: 4,
                        color: '#075bb5',
                        fontWeight: 700
                    }}
                >
                    <span>Área {index + 1}</span>

                    {!readOnly &&
                        <button
                            type='button'
                            title='Remover área da coleta'
                            onClick={() => {
                                registrarExclusaoArea(
                                    fields.get(index)
                                );
                                fields.remove(index);
                            }}
                            style={{
                                width: 34,
                                height: 34,
                                border: 'none',
                                borderRadius: 8,
                                background: '#fff0f1',
                                color: '#dc3545',
                                cursor: 'pointer'
                            }}
                        >
                            <i className='fas fa-trash-alt' />
                        </button>}
                </div>

                <Row>
                    <Select
                        name={`${area}.id_cliente_area`}
                        options={areasCliente}
                        label='Área do cliente *'
                        cols='12 6 4 4'
                        readOnly={readOnly}
                        value={
                            (fields.get(index) || {})
                                .id_cliente_area
                        }
                        onChange={id => {
                            const selecionada =
                                areasCliente.find(item =>
                                    String(item.id) === String(id)
                                );
                            change(
                                `${area}.id_cliente_area`,
                                Number(id)
                            );
                            if (selecionada) {
                                change(
                                    `${area}.area_gps`,
                                    Number(
                                        selecionada.area_hectares
                                    ) || 0
                                );
                            }
                        }}
                    />

                    <Field
                        name={`${area}.id_consultor`}
                        options={consultores}
                        component={Select}
                        label='Consultor *'
                        cols='12 6 4 4'
                        readOnly={readOnly}
                    />

                    <Field
                        name={`${area}.area_gps`}
                        component={LabelAndInputNumber}
                        label='Área GPS (ha) *'
                        placeholder='0,00'
                        cols='12 4 2 2'
                        casas={2}
                        readOnly={readOnly}
                    />
                </Row>

                <Row>
                    <Field
                        name={`${area}.amostras`}
                        component={LabelAndInputNumber}
                        label='Amostras *'
                        placeholder='0'
                        cols='12 4 2 2'
                        casas={0}
                        semSeparadorMilhar
                        readOnly={readOnly}
                    />

                    <Field
                        name={`${area}.tamanho_grid`}
                        component={LabelAndInputNumber}
                        label='Tamanho do grid (ha)'
                        placeholder='0,00'
                        cols='12 4 2 2'
                        casas={4}
                        readOnly='readOnly'
                    />

                    <Field
                        name={`${area}.profundidade`}
                        component={LabelAndInput}
                        label='Profundidade *'
                        placeholder='Ex.: 0-20'
                        cols='12 4 2 2'
                        readOnly={readOnly}
                    />

                    <Field
                        name={`${area}.zona_manejo`}
                        component={LabelAndCheckbox}
                        label='Zona de manejo'
                        cols='12 4 2 2'
                        readOnly={readOnly}
                    />
                </Row>

                <Row>
                    <Field
                        name={`${area}.preco_grid`}
                        component={LabelAndInputNumber}
                        label='Preço por grid *'
                        placeholder='0,00'
                        cols='12 6 3 2'
                        casas={2}
                        readOnly={readOnly}
                    />

                    <Field
                        name={`${area}.valor_total`}
                        component={LabelAndInputNumber}
                        label='Valor total'
                        placeholder='0,00'
                        cols='12 6 3 2'
                        casas={2}
                        readOnly='readOnly'
                    />

                    <Field
                        name={`${area}.valor_desconto`}
                        component={LabelAndInputNumber}
                        label='Desconto'
                        placeholder='0,00'
                        cols='12 6 3 2'
                        casas={2}
                        readOnly={readOnly}
                    />

                    <Field
                        name={`${area}.valor_final`}
                        component={LabelAndInputNumber}
                        label='Valor final'
                        placeholder='0,00'
                        cols='12 6 3 2'
                        casas={2}
                        readOnly='readOnly'
                    />
                </Row>

                <Row>
                    <Field
                        name={`${area}.observacao`}
                        component={LabelAndTextarea}
                        label='Observação da área'
                        placeholder='Informe'
                        cols='12 12 12 12'
                        rows={2}
                        readOnly={readOnly}
                    />
                </Row>

                {mostrarLaboratorio &&
                    <Row>
                        <Field
                            name={`${area}.resultado_laboratorio`}
                            component={LabelAndTextarea}
                            label='Resultado do laboratório *'
                            placeholder='Informe o resultado'
                            cols='12 12 6 6'
                            rows={3}
                            readOnly={laboratorioReadOnly}
                        />

                        <Field
                            name={`${area}.observacao_resultado`}
                            component={LabelAndTextarea}
                            label='Observação do resultado *'
                            placeholder='Informe a observação'
                            cols='12 12 6 6'
                            rows={3}
                            readOnly={laboratorioReadOnly}
                        />
                    </Row>}
            </div>
        ))}
    </div>
    );
};

let ColetaForm = props => {

    const [clienteAberto, setClienteAberto] = useState(false);

    const formulario = props.formularioValues || {};
    const status =
        formulario.status || STATUS.AGUARDANDO_APROVACAO;
    const podeEditarColeta =
        !formulario.id ||
        status === STATUS.AGUARDANDO_APROVACAO;
    const readOnly =
        props.excluir || !podeEditarColeta
            ? 'readOnly'
            : '';
    const statusConfig =
        STATUS_CONFIG[status] ||
        STATUS_CONFIG[STATUS.AGUARDANDO_APROVACAO];
    const usuarioResponsavel = (
        formulario.ids_usuarios_coleta || []
    ).map(String).includes(
        String(props.sessao && props.sessao.id)
    );

    useEffect(() => {
        (formulario.areas || []).forEach((area, index) => {
            const tamanhoGrid = calcularTamanhoGrid(area);
            const valorTotal = calcularValorTotal(area);
            const valorFinal = calcularValorFinal(area);

            if (
                normalizarNumero(area.tamanho_grid) !==
                tamanhoGrid
            ) {
                props.change(
                    `areas[${index}].tamanho_grid`,
                    tamanhoGrid
                );
            }

            if (
                normalizarNumero(area.valor_total) !==
                valorTotal
            ) {
                props.change(
                    `areas[${index}].valor_total`,
                    valorTotal
                );
            }

            if (
                normalizarNumero(area.valor_final) !==
                valorFinal
            ) {
                props.change(
                    `areas[${index}].valor_final`,
                    valorFinal
                );
            }
        });
    }, [formulario.areas, props.change]);

    useEffect(() => {
        props.getHistorico(formulario.id);
    }, [formulario.id]);

    useEffect(() => {
        if (
            !formulario.id &&
            !formulario.id_forma_pagamento
        ) {
            const formaPadrao = props.formasPagamento.find(
                item => item.codigo === 'A_VISTA'
            );

            if (formaPadrao) {
                props.change(
                    'id_forma_pagamento',
                    formaPadrao.id
                );
                props.change('quantidade_parcelas', 1);
            }
        }
    }, [
        formulario.id,
        formulario.id_forma_pagamento,
        props.formasPagamento
    ]);

    const clienteSelecionado = (props.clientes || [])
        .find(item =>
            String(item.id) === String(formulario.id_cliente)
        );

    const totais = (formulario.areas || []).reduce(
        (resumo, area) => ({
            valorTotal:
                resumo.valorTotal +
                normalizarNumero(area.valor_total),
            desconto:
                resumo.desconto +
                normalizarNumero(area.valor_desconto)
        }),
        {
            valorTotal: 0,
            desconto: 0
        }
    );
    const historicoAgrupado = Object.values(
        (props.historico || []).reduce((grupos, item) => {
            const chave = [
                item.id_usuario,
                new Date(item.datahora).getTime()
            ].join('-');

            if (!grupos[chave]) {
                grupos[chave] = {
                    ...item,
                    eventos: []
                };
            }

            grupos[chave].eventos.push(item);

            if (item.entidade === 'COLETA') {
                grupos[chave] = {
                    ...grupos[chave],
                    ...item,
                    eventos: grupos[chave].eventos
                };
            }

            return grupos;
        }, {})
    ).sort(
        (a, b) =>
            new Date(b.datahora).getTime() -
            new Date(a.datahora).getTime()
    );

    const alterarCliente = idCliente => {
        props.initForm({
            ...formulario,
            id_cliente: idCliente,
            areas: []
        });

        props.getAreasCliente(idCliente);
    };

    const registrarExclusaoArea = area => {
        if (!area || !area.id) {
            return;
        }

        props.change(
            'ids_areas_excluidas',
            [
                ...new Set([
                    ...(formulario.ids_areas_excluidas || []),
                    Number(area.id)
                ])
            ]
        );
    };

    const alterarFormaPagamento = idFormaPagamento => {
        const formaPagamento = props.formasPagamento.find(
            item =>
                String(item.id) === String(idFormaPagamento)
        );

        props.change(
            'id_forma_pagamento',
            Number(idFormaPagamento)
        );

        if (
            formaPagamento &&
            !formaPagamento.permite_parcelamento
        ) {
            props.change('quantidade_parcelas', 1);
        }
    };

    const abrirCliente = () => {
        if (!clienteSelecionado) {
            return;
        }

        props.setModoTelaCliente(
            'cadastro',
            clienteSelecionado
        );

        props.initFormCliente(clienteSelecionado);
        props.getListaCidade();
        setClienteAberto(true);
    };

    const fecharCliente = () => {
        props.getOpcoes();
        props.getAreasCliente(formulario.id_cliente);
        setClienteAberto(false);
    };

    if (clienteAberto) {
        return (
            <div>
                <div
                    style={{
                        marginBottom: 14,
                        padding: '12px 16px',
                        borderRadius: 10,
                        background: '#eaf4ff',
                        border: '1px solid #bddcff',
                        color: '#075bb5',
                        fontWeight: 600
                    }}
                >
                    <i
                        className='fas fa-info-circle'
                        style={{ marginRight: 8 }}
                    />
                    Você está editando o cliente da coleta.
                </div>

                <ClienteForm
                    onSubmit={props.salvarCliente}
                    onVoltar={fecharCliente}
                />
            </div>
        );
    }

    return (
        <ContentCard>
            <Form event={props.handleSubmit}>
                <ContentCardBody>
                    {formulario.id &&
                        <React.Fragment>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 12,
                                    marginBottom: 10,
                                    padding: '13px 16px',
                                    borderRadius: 10,
                                    color: statusConfig.cor,
                                    background: statusConfig.fundo,
                                    fontWeight: 700
                                }}
                            >
                                <span>
                                    <i
                                        className='fas fa-stream'
                                        style={{ marginRight: 8 }}
                                    />
                                    Status da coleta
                                </span>

                                <span>{statusConfig.label}</span>
                            </div>

                            <FluxoColeta status={status} />
                        </React.Fragment>}

                    <Row>
                        {formulario.id &&
                            <Field
                                name='numero'
                                component={LabelAndInput}
                                label='Número'
                                cols='12 2 2 2'
                                readOnly='readOnly'
                            />}

                        <Field
                            name='datahora_solicitacao'
                            component={LabelAndInput}
                            label='Data e hora da solicitação *'
                            type='datetime-local'
                            cols='12 4 3 3'
                            readOnly={readOnly}
                        />

                        <Field
                            name='data_previsao_coleta'
                            component={LabelAndInputDate}
                            label='Previsão da coleta *'
                            cols='12 4 3 3'
                            readOnly={readOnly}
                        />
                    </Row>

                    <Row>
                        <Select
                            name='id_cliente'
                            options={props.clientes}
                            label='Cliente *'
                            cols='12 5 4 4'
                            placeholder='Selecione'
                            readOnly={readOnly || !!formulario.id}
                            value={formulario.id_cliente}
                            onChange={alterarCliente}
                        />

                        <Select
                            name='id_veiculo'
                            options={props.veiculos}
                            label='Veículo *'
                            cols='12 4 4 4'
                            placeholder='Selecione'
                            readOnly={readOnly}
                            value={formulario.id_veiculo}
                            onChange={valor =>
                                props.change('id_veiculo', Number(valor))
                            }
                        />

                        {podeEditarColeta && !props.excluir &&
                            <Grid cols='12 3 4 4'>
                                <button
                                    type='button'
                                    disabled={!clienteSelecionado}
                                    onClick={abrirCliente}
                                    style={{
                                        width: '100%',
                                        minHeight: 42,
                                        marginTop: 28,
                                        border: '1px solid #0879df',
                                        borderRadius: 9,
                                        background: clienteSelecionado
                                            ? '#087df1'
                                            : '#dbe2ea',
                                        color: clienteSelecionado
                                            ? '#fff'
                                            : '#7b8794',
                                        cursor: clienteSelecionado
                                            ? 'pointer'
                                            : 'not-allowed',
                                        fontWeight: 600
                                    }}
                                >
                                    <i
                                        className='fas fa-user-edit'
                                        style={{ marginRight: 8 }}
                                    />
                                    Abrir cadastro do cliente
                                </button>
                            </Grid>}
                    </Row>

                    {formulario.id &&
                        <Row>
                            <Select
                                name='ids_usuarios_coleta'
                                options={props.usuariosColeta}
                                label='Responsáveis pela coleta *'
                                cols='12 12 12 12'
                                placeholder='Selecione'
                                multiple
                                readOnly={
                                    props.excluir ||
                                    status !==
                                    STATUS.AGUARDANDO_APROVACAO
                                }
                                value={
                                    formulario.ids_usuarios_coleta ||
                                    []
                                }
                                onChange={itens =>
                                    props.change(
                                        'ids_usuarios_coleta',
                                        (itens || []).map(item =>
                                            item.value
                                        )
                                    )}
                            />
                        </Row>}

                    {formulario.id &&
                        [
                            STATUS.AGUARDANDO_APROVACAO,
                            STATUS.REJEITADO
                        ].includes(status) &&
                        <Row>
                            <Field
                                name='motivo_rejeicao'
                                component={LabelAndTextarea}
                                label={
                                    status === STATUS.REJEITADO
                                        ? 'Motivo da rejeição'
                                        : 'Motivo da rejeição (obrigatório ao rejeitar)'
                                }
                                placeholder='Informe por que a coleta foi rejeitada'
                                cols='12 12 12 12'
                                rows={2}
                                readOnly={
                                    props.excluir ||
                                    status === STATUS.REJEITADO
                                        ? 'readOnly'
                                        : ''
                                }
                            />
                        </Row>}

                    <Row>
                        <Field
                            name='observacao'
                            component={LabelAndTextarea}
                            label='Observação da coleta'
                            placeholder='Informe'
                            cols='12 12 12 12'
                            rows={3}
                            readOnly={readOnly}
                        />
                    </Row>

                    <FieldArray
                        name='areas'
                        component={renderAreas}
                        readOnly={readOnly}
                        areasCliente={props.areasCliente}
                        consultores={props.consultores}
                        idUsuario={props.sessao && props.sessao.id}
                        status={status}
                        excluir={props.excluir}
                        usuarioResponsavel={usuarioResponsavel}
                        registrarExclusaoArea={
                            registrarExclusaoArea
                        }
                        change={props.change}
                    />

                    <Row>
                        <Select
                            name='id_forma_pagamento'
                            options={props.formasPagamento}
                            label='Forma de pagamento *'
                            cols='12 6 4 4'
                            placeholder='Selecione'
                            readOnly={readOnly}
                            value={formulario.id_forma_pagamento}
                            onChange={alterarFormaPagamento}
                        />

                        <Select
                            name='quantidade_parcelas'
                            options={PARCELAS}
                            label='Parcelas *'
                            cols='12 6 3 3'
                            placeholder='Selecione'
                            readOnly={
                                readOnly ||
                                !(
                                    props.formasPagamento.find(item =>
                                        String(item.id) ===
                                        String(formulario.id_forma_pagamento)
                                    ) || {}
                                ).permite_parcelamento
                            }
                            value={formulario.quantidade_parcelas}
                            onChange={valor =>
                                props.change(
                                    'quantidade_parcelas',
                                    Number(valor)
                                )
                            }
                        />
                    </Row>

                    <Row>
                        {Array.from(
                            {
                                length:
                                    Number(
                                        formulario.quantidade_parcelas
                                    ) || 1
                            },
                            (_, indice) => (
                                <Field
                                    key={indice}
                                    name={indice === 0
                                        ? 'data_primeiro_vencimento'
                                        : `datas_vencimentos[${indice}]`}
                                    component={LabelAndInput}
                                    label={
                                        `${indice + 1}º vencimento *`
                                    }
                                    type='date'
                                    cols='12 6 4 3'
                                    readOnly={readOnly}
                                />
                            )
                        )}
                    </Row>

                    <div
                        style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            justifyContent: 'flex-end',
                            gap: 10,
                            marginTop: 18
                        }}
                    >
                        {[
                            {
                                label: 'Valor total',
                                valor: totais.valorTotal
                            },
                            {
                                label: 'Desconto',
                                valor: totais.desconto
                            },
                            {
                                label: 'Valor final',
                                valor:
                                    totais.valorTotal -
                                    totais.desconto
                            }
                        ].map(item => (
                            <div
                                key={item.label}
                                style={{
                                    minWidth: 170,
                                    padding: '12px 14px',
                                    borderRadius: 10,
                                    background: '#f3f7fb',
                                    border: '1px solid #dce3eb'
                                }}
                            >
                                <small className='text-muted'>
                                    {item.label}
                                </small>

                                <div
                                    style={{
                                        fontSize: 18,
                                        fontWeight: 700,
                                        color: '#062042'
                                    }}
                                >
                                    {formatarMoeda(item.valor)}
                                </div>
                            </div>
                        ))}
                    </div>

                    {formulario.id &&
                        <div
                            style={{
                                marginTop: 22,
                                padding: 16,
                                borderRadius: 10,
                                border: '1px solid #dce3eb',
                                background: '#f8fafc'
                            }}
                        >
                            <div
                                style={{
                                    marginBottom: 12,
                                    fontWeight: 700,
                                    color: '#062042'
                                }}
                            >
                                <i
                                    className='fas fa-history'
                                    style={{ marginRight: 8 }}
                                />
                                Histórico de alterações
                            </div>

                            {historicoAgrupado.length === 0 &&
                                <span className='text-muted'>
                                    Nenhuma alteração registrada.
                                </span>}

                            {historicoAgrupado.map(item => {
                                const operacoes = {
                                    INCLUSAO: 'incluiu',
                                    ALTERACAO: 'alterou',
                                    EXCLUSAO: 'excluiu'
                                };
                                const entidade =
                                    item.entidade === 'AREA'
                                        ? 'uma área da coleta'
                                        : 'a coleta';
                                const transicao =
                                    item.status_anterior &&
                                    item.status_novo &&
                                    item.status_anterior !==
                                    item.status_novo
                                        ? ` de ${item.nome_status_anterior} para ${item.nome_status_novo}`
                                        : '';

                                return (
                                    <div
                                        key={item.id}
                                        style={{
                                            padding: '9px 0',
                                            borderTop:
                                                '1px solid #e2e8f0'
                                        }}
                                    >
                                        <strong>
                                            {item.nome_usuario}
                                        </strong>
                                        {' '}
                                        {operacoes[item.operacao] ||
                                            'alterou'}
                                        {' '}
                                        {entidade}
                                        {transicao}.
                                        <div
                                            className='text-muted'
                                            style={{ fontSize: 12 }}
                                        >
                                            {new Date(
                                                item.datahora
                                            ).toLocaleString('pt-BR')}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>}
                </ContentCardBody>

                <ContentCardFooter>
                    <Row alignCenter>
                        <Grid cols='12 6 4 3'>
                            <Button
                                text='Voltar'
                                type='light'
                                icon='fa fa-chevron-left'
                                event={() =>
                                    props.setModoTela('lista')
                                }
                            />
                        </Grid>

                        <Grid cols='12 6 4 3'>
                            {props.excluir &&
                                <Button
                                    text='Excluir'
                                    submit
                                    type='danger'
                                    icon='fa fa-trash'
                                />}

                            {!props.excluir && podeEditarColeta &&
                                <Button
                                    text={
                                        formulario.id
                                            ? 'Salvar alterações'
                                            : 'Criar coleta'
                                    }
                                    submit
                                    type='success'
                                    icon='fa fa-check'
                                />}
                        </Grid>

                        {!props.excluir &&
                            formulario.id &&
                            status !== STATUS.AGUARDANDO_APROVACAO &&
                            <Grid cols='12 6 4 3'>
                                <Button
                                    text='Reabrir para ajustes'
                                    type='warning'
                                    icon='fas fa-undo'
                                    event={() =>
                                        props.alterarStatus(
                                            formulario,
                                            STATUS.AGUARDANDO_APROVACAO
                                        )}
                                />
                            </Grid>}

                        {!props.excluir &&
                            formulario.id &&
                            status === STATUS.AGUARDANDO_APROVACAO &&
                            <React.Fragment>
                                <Grid cols='12 6 4 3'>
                                    <Button
                                        text='Rejeitar'
                                        type='danger'
                                        icon='fas fa-times'
                                        event={() =>
                                            props.alterarStatus(
                                                formulario,
                                                STATUS.REJEITADO
                                            )}
                                    />
                                </Grid>

                                <Grid cols='12 6 4 3'>
                                    <Button
                                        text='Aprovar coleta'
                                        type='success'
                                        icon='fas fa-thumbs-up'
                                        event={() =>
                                            props.alterarStatus(
                                                formulario,
                                                STATUS.AGUARDANDO_COLETA
                                            )}
                                    />
                                </Grid>
                            </React.Fragment>}

                        {!props.excluir &&
                            formulario.id &&
                            status === STATUS.AGUARDANDO_COLETA &&
                            <Grid cols='12 6 4 3'>
                                <Button
                                    text='Finalizar coleta'
                                    type='success'
                                    icon='fas fa-check-circle'
                                    disabled={!usuarioResponsavel}
                                    event={() =>
                                        props.alterarStatus(
                                            formulario,
                                            STATUS.COLETADO
                                        )}
                                />
                            </Grid>}

                        {!props.excluir &&
                            formulario.id &&
                            status === STATUS.COLETADO &&
                            <Grid cols='12 6 4 3'>
                                <Button
                                    text='Enviar ao laboratório'
                                    type='info'
                                    icon='fas fa-flask'
                                    event={() =>
                                        props.alterarStatus(
                                            formulario,
                                            STATUS.LABORATORIO
                                        )}
                                />
                            </Grid>}

                        {!props.excluir &&
                            formulario.id &&
                            status === STATUS.LABORATORIO &&
                            <Grid cols='12 6 4 3'>
                                <Button
                                    text='Finalizar'
                                    type='success'
                                    icon='fas fa-flag-checkered'
                                    event={() =>
                                        props.alterarStatus(
                                            formulario,
                                            STATUS.FINALIZADA
                                        )}
                                />
                            </Grid>}
                    </Row>
                </ContentCardFooter>
            </Form>
        </ContentCard>
    );
};

ColetaForm = reduxForm({
    form: 'coletaForm',
    destroyOnUnmount: false
})(ColetaForm);

const mapStateToProps = state => ({
    sessao: state.auth.usuario,
    formularioValues:
        getFormValues('coletaForm')(state),
    clientes: state.coleta.clientes.map(item => ({
        ...item,
        valor: item.razao_social
    })),
    consultores: state.coleta.consultores.map(item => ({
        ...item,
        valor: item.nome
    })),
    usuariosColeta: state.coleta.usuariosColeta.map(item => ({
        ...item,
        valor: item.nome
    })),
    veiculos: state.coleta.veiculos.map(item => ({
        ...item,
        valor: item.placa_nome
    })),
    formasPagamento: state.coleta.formasPagamento.map(item => ({
        ...item,
        valor: item.descricao
    })),
    planosReceita: state.coleta.planosReceita,
    historico: state.coleta.historico,
    areasCliente: state.coleta.areasCliente.map(item => ({
        ...item,
        valor: item.nome
    }))
});

const mapDispatchToProps = dispatch =>
    bindActionCreators({
        setModoTela,
        initForm,
        getOpcoes,
        getAreasCliente,
        getHistorico,
        alterarStatus,
        setModoTelaCliente,
        initFormCliente,
        salvarCliente,
        getListaCidade
    }, dispatch);

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(ColetaForm);
